import React from 'react';
import { useBusiness } from '../context/BusinessContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, UserCog, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';

const AccountSettingsPage = () => {
  const { currentBusiness, setCurrentBusiness } = useBusiness();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const handleLogout = async () => {
    setCurrentBusiness(null);
    localStorage.removeItem('currentBusiness');
    await signOut();
    navigate('/');
  };

  const handleDelete = async () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      await api.deleteBusiness(currentBusiness.business_id);
      setCurrentBusiness(null);
      setShowDeleteModal(false);
      navigate('/');
    } catch (error) {
      alert('Failed to delete business. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">No business found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-10 px-4">
      <div className="w-full max-w-xl bg-white rounded-lg shadow-lg p-6 relative">
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
          onClick={() => navigate(-1)}
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <UserCog className="w-6 h-6" />
          Account Settings
        </h2>
        <div className="space-y-3">
          <div>
            <span className="font-semibold text-gray-700">Email:</span>
            <span className="ml-2 text-gray-900">{currentBusiness?.email || '-'}</span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Business Name:</span>
            <span className="ml-2 text-gray-900">{currentBusiness?.name || '-'}</span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Category:</span>
            <span className="ml-2 text-gray-900">
              {currentBusiness?.settings?.businessType || currentBusiness?.primary_type || '-'}
            </span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Address:</span>
            <span className="ml-2 text-gray-900">
              {currentBusiness?.settings?.address || currentBusiness?.address || '-'}
            </span>
          </div>
          <div>
            <span className="font-semibold text-gray-700">Google Place ID:</span>
            <span className="ml-2 text-gray-900">
              {currentBusiness?.settings?.placeId || currentBusiness?.place_id || '-'}
            </span>
          </div>
        </div>
        <div className="flex justify-end items-center mt-6">
          <button
            onClick={handleDelete}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium"
          >
            Delete Account
          </button>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Delete Business</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete your business? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium flex items-center"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  <>Delete</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettingsPage;

